import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  createSharedLinkEffect,
  getSharedAudioListEffect,
  revokeSharedLinkEffect,
  updateSharedLinkEffect,
} from 'app/store/Shares/effects';
import {
  getSharedAudioLinks,
  getSharedLinkListNextLink,
  isAudioShareModalOpen,
} from 'app/store/Shares/selectors';
import { Link } from 'types/api.types';
import { ShareLinkDialog } from 'app/components/Shares/LinkDialog';
import { UpdateSharedLinkEffectData } from 'app/store/Shares/effects.types';
import { previewSharedAudioAction } from 'app/store/Mixpanel/slice';
import { ApiErrorCodeTypes } from 'types/enums';
import { setShareModal } from 'app/store/Shares/slice';
import { useShowUpgradePopup } from './utils';

interface AudioShareLinkDialogProps {
  onClose: () => void;
  audioTitle: string;
  audioGuid: string;
  songGuid: string;
}

export const AudioShareLinkDialog: React.FC<AudioShareLinkDialogProps> = ({
  audioTitle,
  audioGuid,
  songGuid,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(isAudioShareModalOpen);

  const {
    updateShareLinkAllowed,
    revokeAllowed,
    showUpgradePopupForUpdateShareLink,
    showUpgradePopupForRevokeShareLink,
  } = useShowUpgradePopup({ audioGuid });
  const links = useAppSelector((state) =>
    getSharedAudioLinks(state, audioGuid)
  );
  const [isLoading, setIsLoading] = useState(false);

  const addLink = async (title: string) =>
    dispatch(createSharedLinkEffect({ title, audioGuid, songGuid })).unwrap();

  const revokeLink = async (linkGuid: string, link: Link) => {
    if (!revokeAllowed()) {
      return;
    }

    try {
      await dispatch(
        revokeSharedLinkEffect({ linkGuid, link, guid: audioGuid })
      ).unwrap();
    } catch (e: any) {
      if (e?.errorCode === ApiErrorCodeTypes.LIMIT_REACHED) {
        showUpgradePopupForRevokeShareLink();
      }
    }
  };

  const updateSharedLink = async (
    link: Link,
    data: UpdateSharedLinkEffectData['data']
  ) => {
    if (!updateShareLinkAllowed(data)) {
      return Promise.resolve();
    }

    try {
      return await dispatch(
        updateSharedLinkEffect({
          link,
          guid: audioGuid,
          data,
        })
      ).unwrap();
    } catch (e: any) {
      if (e?.errorCode === ApiErrorCodeTypes.LIMIT_REACHED) {
        showUpgradePopupForUpdateShareLink(data);
      }
      throw e;
    }
  };

  const fetchSharedLinks = useCallback(async () => {
    try {
      setIsLoading(true);
      await dispatch(getSharedAudioListEffect({ guid: audioGuid })).unwrap();
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [audioGuid, dispatch]);

  useEffect(() => {
    dispatch(setShareModal({ type: 'audio', isOpen: true }));
    fetchSharedLinks();
  }, [dispatch, fetchSharedLinks]);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    } else if (isMounted) {
      onClose();
    }
  }, [isMounted, isOpen, onClose]);

  return (
    <ShareLinkDialog
      isOpen={isOpen}
      onClose={() => dispatch(setShareModal({ isOpen: false }))}
      title={audioTitle}
      addLink={addLink}
      revokeLink={revokeLink}
      updateSharedLink={updateSharedLink}
      sharedLinks={links || []}
      isLoading={isLoading}
      guid={audioGuid}
      getSharedListEffect={getSharedAudioListEffect}
      getSharedLinkListNextLink={getSharedLinkListNextLink}
      onPreviewClick={({
        guid: songGuid,
        creditsShared,
        lyricsShared,
        downloadable,
      }) => {
        dispatch(
          previewSharedAudioAction({
            songGuid,
            creditsShared,
            lyricsShared,
            downloadable,
          })
        );
      }}
    />
  );
};
